import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Archive, Shield, Users, FileText, Clock, Search } from 'lucide-react';

const Home = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <Archive className="w-8 h-8" />,
      title: "Archivage Centralisé",
      description: "Conservez tous vos documents importants du Dahira en un seul endroit sécurisé."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Sécurité Garantie",
      description: "Vos documents sont protégés avec un système d'authentification robuste."
    },
    {
      icon: <Search className="w-8 h-8" />,
      title: "Recherche Facilitée",
      description: "Retrouvez rapidement vos documents grâce à notre système de recherche avancé."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Gestion des Commissions",
      description: "Organisez les documents par commission pour une meilleure collaboration."
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: "Multi-formats",
      description: "Gérez tous types de documents : ZIP, PDF, images, documents Word et plus encore."
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Historique Complet",
      description: "Suivez l'évolution et les modifications de vos documents dans le temps."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white rounded-xl shadow-md p-2 flex items-center justify-center">
                <img
                  src="/logo-touba-rennes.jpg"
                  alt="Touba Rennes"
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Archive Touba Rennes</h1>
                <p className="text-sm text-gray-600">Système de gestion documentaire</p>
              </div>
            </div>
            <div className="hidden sm:flex gap-3">
              <button
                onClick={() => navigate('/login')}
                className="px-6 py-2 text-primary-700 font-semibold hover:text-primary-800 transition-colors"
              >
                Se connecter
              </button>
              <button
                onClick={() => navigate('/register')}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors shadow-sm"
              >
                Créer un compte
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-primary-700 rounded-full text-sm font-semibold mb-6">
            <Archive className="w-4 h-4" />
            <span>Solution d'archivage pour le dahira Touba Rennes</span>
          </div>

          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Gérez vos documents
            <br />
            <span className="text-primary-600">en toute simplicité</span>
          </h2>

          <p className="text-xl text-gray-600 mb-12 max-w-3xl mx-auto leading-relaxed">
            Une plateforme moderne et sécurisée pour archiver, organiser et partager
            les documents du Dahira Tayssiroul Hassir de Touba Rennes. Accessible depuis n'importe quel appareil.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={() => navigate('/register')}
              className="w-full sm:w-auto px-8 py-4 bg-primary-600 text-white rounded-xl font-bold text-lg hover:bg-primary-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              Créer un compte gratuitement
            </button>
            <button
              onClick={() => navigate('/login')}
              className="w-full sm:w-auto px-8 py-4 bg-white text-primary-700 rounded-xl font-bold text-lg border-2 border-primary-600 hover:bg-primary-50 transition-all"
            >
              Se connecter
            </button>
          </div>

          {/* Trust Badge */}
          <div className="mt-12 flex items-center justify-center gap-2 text-sm text-gray-500">
            <Shield className="w-5 h-5 text-primary-600" />
            <span>Plateforme sécurisée et approuvée par le Dahira</span>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="text-center mb-16">
          <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Fonctionnalités principales
          </h3>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Tout ce dont vous avez besoin pour gérer efficacement les archives du dahira
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-all border border-gray-100 hover:border-primary-200"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center text-white mb-6 shadow-lg">
                {feature.icon}
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                {feature.title}
              </h4>
              <p className="text-gray-600 leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-3xl p-12 sm:p-16 text-center shadow-2xl">
          <h3 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Prêt à commencer ?
          </h3>
          <p className="text-xl text-primary-50 mb-10 max-w-2xl mx-auto">
            Rejoignez la plateforme dès aujourd'hui et commencez à organiser vos documents
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={() => navigate('/register')}
              className="w-full sm:w-auto px-10 py-4 bg-white text-primary-700 rounded-xl font-bold text-lg hover:bg-gray-50 transition-all shadow-lg"
            >
              Créer un compte
            </button>
            <button
              onClick={() => navigate('/login')}
              className="w-full sm:w-auto px-10 py-4 bg-primary-800 text-white rounded-xl font-bold text-lg border-2 border-white/20 hover:bg-primary-900 transition-all"
            >
              Se connecter
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-200 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-12 bg-white rounded-lg shadow p-2 flex items-center justify-center">
                <img
                  src="/logo-touba-rennes.jpg"
                  alt="Touba Rennes"
                  className="w-full h-full object-contain"
                />
              </div>
              <span className="text-xl font-bold text-gray-900">Archive Touba Rennes</span>
            </div>
            <div className="text-sm text-gray-500">
              © {new Date().getFullYear()} Archive Touba Rennes. Tous droits réservés.
            </div>
            <div className="mt-4">
              <a
                href="https://toubarennes.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                toubarennes.com
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
